/**
 * How a reel from /stories/reels becomes a PortalRing — shared by the Portals rail on the
 * wall, the rail in Messages, thread rows, the thread header and the new-message modal.
 * One place, so a person looks the same everywhere they appear.
 */

// How long is left, in the countdown pill's voice: "3H 41M", then "41M", then "2M".
export function countdownLabel(iso) {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - Date.now();
  if (Number.isNaN(ms) || ms <= 0) return null;
  const mins = Math.floor(ms / 60000);
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h > 0) return `${h}H ${m}M`;
  return `${Math.max(1, m)}M`;
}

/** Whole days left, the way the introduction counts them: 7 on the day it goes up. */
export function introDaysLeft(iso, now = Date.now()) {
  const ms = new Date(iso).getTime() - now;
  if (Number.isNaN(ms) || ms <= 0) return 0;
  return Math.min(7, Math.ceil(ms / 86400000));
}

/** A reel the rail draws as an introduction: one is up and no Portal is taking the ring. */
export const isIntroReel = (reel) => !!reel?.introduction && !reel?.portal;

/** What a page fires when someone's introduction should leave the rail behind a closed viewer. */
export const REELS_CHANGED = 'pr-reels-changed';

export function ringStateFor(reel) {
  if (isIntroReel(reel)) return 'intro';
  if (!reel.HasUnseen && !reel.hasUnseen) return 'seen';
  if (reel.isClub) return 'club';
  if (reel.portal && !reel.portal.media) return 'text';
  if (reel.expiresAtUtc && countdownLabel(reel.expiresAtUtc)) return 'live';
  return 'unseen';
}

/** The face in the ring: the Portal's own cover frame when it has one, else the person. */
export function ringFace(reel) {
  const hero = reel.portal?.media;
  if (hero?.thumbnailUrl) return { url: hero.thumbnailUrl, focus: hero.coverFocus || null };
  return { url: reel.avatarUrl, focus: null };
}

/**
 * The town a Portal is hosted in, for the ring's city tab (rail handoff, option 1b): the first
 * part of the Portal's city label, upper-cased. Null when the Portal has no city.
 */
export function ringCity(reel) {
  const raw = reel?.portal?.city || '';
  const t = raw.split(',')[0].trim();
  return t ? t.toUpperCase() : null;
}

/** The tab's tone: cyan for a Portal sent to the City Portal, grey once watched, else white. */
export function cityTone(reel) {
  if (!reel) return 'default';
  if (!reel.HasUnseen && !reel.hasUnseen) return 'seen';
  const p = reel.portal;
  const rooms = Array.isArray(p?.rooms) ? p.rooms : [];
  return p?.room === 'public' || rooms.includes('public') ? 'square' : 'default';
}

export function firstWords(text, max = 22) {
  if (!text) return '';
  const t = text.trim();
  return t.length <= max ? `“${t}”` : `“${t.slice(0, max).trim()}…”`;
}

/** Still up: not expired (or no expiry at all). */
export function reelIsLive(reel) {
  return !reel.expiresAtUtc || new Date(reel.expiresAtUtc).getTime() > Date.now();
}

/**
 * Everything a PortalRing needs for this person, or null when they have nothing up.
 * `{ state, countdown, snippet, avatarUrl, focus, initial }` — spread onto the ring.
 */
export function ringPropsFor(reel) {
  if (!reel || !reelIsLive(reel)) return null;
  const face = ringFace(reel);
  return {
    state: ringStateFor(reel),
    countdown: countdownLabel(reel.expiresAtUtc),
    snippet: firstWords(reel.portal?.text),
    avatarUrl: face.url,
    focus: face.focus,
    initial: (reel.displayName || '?')[0],
  };
}

/** The tag on an introduction's ring: YOU, NEW for a recent joiner, else none. */
export function introTag(reel) {
  if (reel?.isOwn) return 'YOU';
  return reel?.introduction?.isNew ? 'NEW' : null;
}

/** "6 DAYS" / "1 DAY" under an introduction's name. */
export function introSub(reel) {
  const n = introDaysLeft(reel?.introduction?.expiresAtUtc);
  return n ? `${n} ${n === 1 ? 'DAY' : 'DAYS'}` : null;
}
